import React, { useRef, useState } from "react";
import "../styles/TransposBoard.css";
import { GameStyleType, Line } from "./types/types";
import { useGame } from "../GameContext";
import shuffleArray from "./hooks/shuffleArray";
import prepareRounds from "./hooks/prepareRounds";
import RenderBoard from "./hooks/RenderBoard";
import InputBoard from "./hooks/InputBoard";
import RenderBoardLogo from "./hooks/RenderBoardLogo";
import MESSAGES from "./hooks/message";

const Board: React.FC<GameStyleType> = ({ random, rounds, help, type }) => {
  const {
    setMessage,
    setGameStarted,
    setIsTimerRunning,
    isTimerRunning,
    isGameStarted,
    time,
    setTime,
  } = useGame();

  // состояние для хранения раундов игры, перемешанных или в исходном порядке
  const [gameRounds, setGameRounds] = useState<
    {
      textBySyllables: string[];
      correctAnswer: string;
    }[]
  >(() => {
    const preparedRounds = prepareRounds(rounds, type);
    return random ? shuffleArray(preparedRounds) : preparedRounds;
  });

  //количество раундов, которые прошел пользователь
  const [roundCount, setRoundCount] = useState<number>(0);

  // проверка, является ли текущий раунд последним
  const isLastRound = roundCount === gameRounds.length - 1;

  // состояние для отслеживания, завершена ли игра
  const [isGameFinished, setIsGameFinished] = useState<boolean>(false);

  // получение текущего раунда игры
  const currentRound = gameRounds[roundCount];

  // функция для получения правильных строк без разделителей
  const getCorrectLines = (round: typeof currentRound) =>
    round.textBySyllables.map((line) => line.replaceAll("|", ""));

  // строки, которые нужно расшифровать, без разделителей
  const correctLines = getCorrectLines(currentRound);

  // состояние для хранения введенных пользователем строк и их статусов
  const [lines, setLines] = useState<Line[]>(
    correctLines.map(() => ({
      value: "",
      status: "idle",
    })),
  );

  // состояние для хранения введенного пользователем ответа
  const [answerValue, setAnswerValue] = useState<string>("");

  // состояние для отслеживания, нужно ли показывать слоги
  const [showSyllables, setShowSyllables] = useState(false);
  const [pressedCheck, setPressedCheck] = useState<boolean>(false);

  // состояние ответа на проверку отгадки
  const [answerStatus, setAnswerStatus] = useState<
    "idle" | "correct" | "wrong"
  >("idle");

  // состояние для отслеживания, угаданы ли все строки
  const [allWordsGuessed, setAllWordsGuessed] = useState<boolean>(false);

  // индекс строки, которую нужно расшифровать
  const activeLine = lines.findIndex((line) => line.status !== "correct");

  // ссылка на массив input элементов для управления фокусом
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // функция для нормализации строки: убирает лишние пробелы и приводит к нижнему регистру
  const normalize = (text: string) =>
    text.trim().replace(/\s+/g, " ").toLowerCase();

  //==========================================
  // Функция для проверки введенной строки на соответствие правильной строке
  //==========================================
  const checkLine = (index: number): boolean => {
    const copy = [...lines];

    if (!copy[index].value.trim()) {
      copy[index].status = "wrong";
      setMessage(MESSAGES.ENTER_LINE);
      setLines(copy);
      return false;
    }

    const correct =
      normalize(lines[index].value) === normalize(correctLines[index]);

    if (correct) {
      copy[index].status = "correct";
      setMessage(MESSAGES.LINE_CORRECT);
    } else {
      copy[index].status = "wrong";
      setMessage(MESSAGES.LINE_WRONG);
    }
    if (copy.every((line) => line.status === "correct")) {
      setAllWordsGuessed(true);
      setMessage(MESSAGES.ALL_LINES_CORRECT);
    }
    setLines(copy);
    return correct;
  };
  //==========================================
  // Функция для проверки введенного ответа на соответствие правильному ответу
  //==========================================
  const checkAnswer = (answer: string) => {
    if (!answer.trim()) {
      setMessage(MESSAGES.ENTER_ANSWER);
      setAnswerStatus("wrong");
      return;
    }

    const correctAnswer = normalize(currentRound.correctAnswer);
    const userAnswer = normalize(answer);
    if (userAnswer === correctAnswer) {
      if (isLastRound) {
        setMessage(MESSAGES.GAME_FINISHED);
        setIsGameFinished(true);
      } else {
        setMessage(MESSAGES.ROUND_FINISHED);
      }
      setIsTimerRunning(false);
      setAnswerStatus("correct");
    } else {
      setMessage(MESSAGES.ANSWER_WRONG);
      setAnswerStatus("wrong");
    }
  };
  //==========================================
  // Функция для перехода к следующему раунду или перезапуска игры
  //==========================================
  const resetRound = (round: typeof currentRound) => {
    setLines(
      getCorrectLines(round).map(() => ({
        value: "",
        status: "idle",
      })),
    );

    setAnswerValue("");
    setAnswerStatus("idle");
    setAllWordsGuessed(false);
    setShowSyllables(false);

    setMessage(MESSAGES.START);
    setIsTimerRunning(true);
  };

  //==========================================
  // Функция для перехода к следующему раунду или перезапуска игры
  //==========================================

  const nextGuessed = () => {
    if (isGameFinished) {
      const newRounds = random ? shuffleArray(rounds) : rounds;

      setGameRounds(newRounds);
      setRoundCount(0);
      setTime(0);
      setIsGameFinished(false);

      resetRound(newRounds[0]);
      return;
    }

    const nextRound = roundCount + 1;

    setRoundCount(nextRound);

    resetRound(gameRounds[nextRound]);
  };

  const handleStart = () => {
    setGameStarted(true);
    setIsTimerRunning(true);
    setMessage(MESSAGES.START);
  };
  return (
    <div className="transpos-Board">
      <div className="transpos-board-content">
        {/* Логотип с кнопками и таймером */}
        <RenderBoardLogo
          isGameStarted={isGameStarted}
          time={time}
          isTimerRunning={isTimerRunning}
          isGameFinished={isGameFinished}
          onNext={nextGuessed}
          onStart={handleStart}
        ></RenderBoardLogo>
        {/* общее игровое поле, два окна */}
        <div className="transpos-board-game">
          {/* поле с загадкой и кнопкой по флагу*/}
          <div className="transpos-board-wrapper">
            <div className="transpos-board-area">
              <div
                className={
                  "transpos-card" +
                  (isGameFinished ? " transpos-card-flipped" : "")
                }
              >
                <div className="transpos-card-face transpos-card-front">
                  {isGameStarted && (
                    <RenderBoard
                      gameRounds={gameRounds}
                      roundCount={roundCount}
                      showSyllables={showSyllables}
                      type={type}
                    />
                  )}
                </div>

                <div className="transpos-card-face transpos-card-back">
                  {/* Картинка после победы */}
                  <img
                    src="/img/crypto/winner.svg"
                    alt="Победа!"
                    className="transpos-win-image"
                  />
                </div>
              </div>
            </div>
            <div className="transpos-board-button">
              {help && (
                <button
                  className="transpos-buttonCheck"
                  onClick={() => {
                    if (isGameStarted) setShowSyllables(true);
                  }}
                >
                  {type === "1" ? "Разбить на слога" : "Разбить на блоки"}
                </button>
              )}
            </div>
          </div>
          {/* поля для расшифровки с кнопками проверки строк,кнопка и поле для проверки отгадки */}
          <div className="transpos-board-wrapper">
            <div className="transpos-input-area">
              <div className="transpos-grid-area">
                {isGameStarted && (
                  <InputBoard
                    lines={lines}
                    setLines={setLines}
                    activeLine={activeLine}
                    checkLine={checkLine}
                    setMessage={setMessage}
                    inputRefs={inputRefs}
                  />
                )}
              </div>

              <div className="transpos-controls">
                {lines.map((line, index) => (
                  <div key={index}>
                    {line.status === "active" && (
                      <img
                        className="transpos-icon"
                        src={
                          pressedCheck
                            ? "/img/transpos/check_2.svg"
                            : "/img/transpos/check_1.svg"
                        }
                        alt="Проверить"
                        title="Проверить строчку"
                        onMouseDown={(event) => {
                          event.preventDefault();

                          if (checkLine(index)) {
                            requestAnimationFrame(() => {
                              inputRefs.current[index + 1]?.focus();
                            });
                          }
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="transpos-button-input-board">
              <div className="transpos-subtitle">Введи ответ: </div>
              <input
                className={"transpos-input transpos-" + answerStatus}
                disabled={!allWordsGuessed || answerStatus === "correct"}
                value={answerValue}
                onFocus={() => setAnswerStatus("idle")}
                onChange={(e) => {
                  setAnswerValue(e.target.value);
                  if (answerStatus === "wrong") {
                    setAnswerStatus("idle");
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.repeat) {
                    event.preventDefault();

                    checkAnswer(answerValue);

                    event.currentTarget.blur();
                  }
                }}
              ></input>
              {allWordsGuessed && !(answerStatus === "correct") && (
                <img
                  className="transpos-icon"
                  alt="Проверить"
                  title="Проверить ответ"
                  src={
                    pressedCheck
                      ? "/img/transpos/check_2.svg"
                      : "/img/transpos/check_1.svg"
                  }
                  onClick={() => {
                    setPressedCheck(true);
                    checkAnswer(answerValue);
                    setTimeout(() => {
                      setPressedCheck(false);
                    }, 150);
                  }}
                />
              )}
              {!isTimerRunning &&isGameStarted && !isGameFinished && (
              <button className="tableS-buttonCheck" onClick={nextGuessed}>
                Далее
                <img
                  alt=""
                  className="tableS-logoRun"
                  src="/img/button/run-fix.svg"
                />
              </button>
            )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Board;
